import React, { useState, useEffect } from 'react';
import { Customer, UpdateCustomerRequest, CustomerStatus, CustomerType, ServiceType } from '../../types';
import { useCustomerStore } from '../../stores/customerStore';
import './CustomerDialogs.css';

interface EditCustomerDialogProps {
  customer: Customer;
  open: boolean;
  onClose: () => void;
}

const EditCustomerDialog: React.FC<EditCustomerDialogProps> = ({ customer, open, onClose }) => {
  const { updateCustomer } = useCustomerStore();
  
  const [formData, setFormData] = useState<UpdateCustomerRequest>({});

  useEffect(() => {
    if (open) {
      setFormData({
        customer_type: customer.customer_type,
        first_name: customer.first_name,
        last_name: customer.last_name,
        company_name: customer.company_name,
        ico: customer.ico,
        dic: customer.dic,
        dic_dph: customer.dic_dph,
        address: customer.address,
        city: customer.city,
        zip_code: customer.zip_code,
        email: customer.email,
        phone: customer.phone,
        latitude: customer.latitude,
        longitude: customer.longitude,
        service_type: customer.service_type,
        service_speed: customer.service_speed,
        status: customer.status,
        ont_serial: customer.ont_serial,
        ont_mac: customer.ont_mac,
        vlan_id: customer.vlan_id,
        ip_address: customer.ip_address,
        port_number: customer.port_number,
        monthly_fee: customer.monthly_fee,
        billing_day: customer.billing_day,
        customer_notes: customer.customer_notes,
        internal_notes: customer.internal_notes,
      });
    }
  }, [open, customer]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateCustomer(customer.id, formData);
      alert('Zákazník bol úspešne upravený');
      onClose();
    } catch (error) {
      alert('Chyba pri úprave zákazníka');
    }
  };

  const handleChange = (field: keyof UpdateCustomerRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="dialog-overlay">
      <div className="dialog-container">
        <div className="dialog-header">
          <h2>Upraviť zákazníka</h2>
          <button onClick={onClose} className="dialog-close">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="dialog-content">
            {/* Customer Type */}
            <div className="form-section">
              <div className="form-group">
                <label>Typ zákazníka <span className="required">*</span></label>
                <select
                  value={formData.customer_type}
                  onChange={(e) => handleChange('customer_type', e.target.value as CustomerType)}
                  required
                >
                  <option value="individual">Fyzická osoba</option>
                  <option value="company">Firma</option>
                </select>
              </div>
            </div>

            {/* Personal/Company Info */}
            <div className="form-section">
              {formData.customer_type === 'individual' ? (
                <div className="form-grid cols-2">
                  <div className="form-group">
                    <label>Meno <span className="required">*</span></label>
                    <input
                      type="text"
                      value={formData.first_name || ''}
                      onChange={(e) => handleChange('first_name', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Priezvisko <span className="required">*</span></label>
                    <input
                      type="text"
                      value={formData.last_name || ''}
                      onChange={(e) => handleChange('last_name', e.target.value)}
                      required
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label>Názov firmy <span className="required">*</span></label>
                    <input
                      type="text"
                      value={formData.company_name || ''}
                      onChange={(e) => handleChange('company_name', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-grid cols-3">
                    <div className="form-group">
                      <label>IČO</label>
                      <input
                        type="text"
                        value={formData.ico || ''}
                        onChange={(e) => handleChange('ico', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>DIČ</label>
                      <input
                        type="text"
                        value={formData.dic || ''}
                        onChange={(e) => handleChange('dic', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>IČ DPH</label>
                      <input
                        type="text"
                        value={formData.dic_dph || ''}
                        onChange={(e) => handleChange('dic_dph', e.target.value)}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Address */}
            <div className="form-section">
              <h3>Adresa</h3>
              <div className="form-group">
                <label>Ulica a číslo <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.address || ''}
                  onChange={(e) => handleChange('address', e.target.value)}
                  required
                />
              </div>
              <div className="form-grid cols-2">
                <div className="form-group">
                  <label>Mesto <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.city || ''}
                    onChange={(e) => handleChange('city', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>PSČ <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.zip_code || ''}
                    onChange={(e) => handleChange('zip_code', e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="form-section">
              <h3>Kontakt</h3>
              <div className="form-grid cols-2">
                <div className="form-group">
                  <label>Email <span className="required">*</span></label>
                  <input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Telefón <span className="required">*</span></label>
                  <input
                    type="tel"
                    value={formData.phone || ''}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Service */}
            <div className="form-section">
              <h3>Služba</h3>
              <div className="form-grid cols-2">
                <div className="form-group">
                  <label>Typ služby <span className="required">*</span></label>
                  <select
                    value={formData.service_type}
                    onChange={(e) => handleChange('service_type', e.target.value as ServiceType)}
                    required
                  >
                    <option value="internet">Internet</option>
                    <option value="internet_tv">Internet + TV</option>
                    <option value="internet_tv_phone">Internet + TV + Telefón</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Rýchlosť</label>
                  <input
                    type="text"
                    value={formData.service_speed || ''}
                    onChange={(e) => handleChange('service_speed', e.target.value)}
                    placeholder="napr. 100/100 Mbps"
                  />
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="form-section">
              <div className="form-group">
                <label>Stav <span className="required">*</span></label>
                <select
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value as CustomerStatus)}
                  required
                >
                  <option value="pending_installation">Čaká na inštaláciu</option>
                  <option value="active">Aktívny</option>
                  <option value="suspended">Pozastavený</option>
                  <option value="terminated">Ukončený</option>
                </select>
              </div>
            </div>

            {/* Technical Details */}
            <div className="form-section">
              <h3>Technické údaje</h3>
              <div className="form-grid cols-2">
                <div className="form-group">
                  <label>ONT Serial</label>
                  <input
                    type="text"
                    value={formData.ont_serial || ''}
                    onChange={(e) => handleChange('ont_serial', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>ONT MAC</label>
                  <input
                    type="text"
                    value={formData.ont_mac || ''}
                    onChange={(e) => handleChange('ont_mac', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>VLAN ID</label>
                  <input
                    type="number"
                    value={formData.vlan_id || ''}
                    onChange={(e) => handleChange('vlan_id', e.target.value ? parseInt(e.target.value) : undefined)}
                  />
                </div>
                <div className="form-group">
                  <label>IP adresa</label>
                  <input
                    type="text"
                    value={formData.ip_address || ''}
                    onChange={(e) => handleChange('ip_address', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Billing */}
            <div className="form-section">
              <h3>Fakturácia</h3>
              <div className="form-grid cols-2">
                <div className="form-group">
                  <label>Mesačný poplatok (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.monthly_fee || ''}
                    onChange={(e) => handleChange('monthly_fee', e.target.value ? parseFloat(e.target.value) : undefined)}
                  />
                </div>
                <div className="form-group">
                  <label>Deň fakturácie (1-31)</label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={formData.billing_day || ''}
                    onChange={(e) => handleChange('billing_day', e.target.value ? parseInt(e.target.value) : undefined)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="dialog-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Zrušiť
            </button>
            <button type="submit" className="btn btn-primary">
              Uložiť
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCustomerDialog;

